import { type Ref, type RefObject, useLayoutEffect, useRef } from "react";

/**
 * Gives uikit an input ref it can rely on while still populating the caller's.
 *
 * uikit's `InputText` uses `innerRef` internally to restore the caret after a
 * controlled parent rejects a keystroke, so it needs a real `RefObject` with a
 * live `.current`. Handing it React Hook Form's callback ref would leave
 * `.current` undefined and silently disable that. This owns the object uikit
 * gets and mirrors the node onto whatever the caller passed, so both the caret
 * logic and imperative `.focus()` calls (SsnInput, PhoneNumberInput
 * auto-advance) keep working.
 *
 * No dependency array: the effect re-syncs every render so a callback ref that
 * changes identity — RHF re-creates one per render — never goes stale.
 * @param external Ref supplied by the caller, of either shape.
 * @returns {RefObject<HTMLInputElement | null>} The ref to hand to uikit's `innerRef`.
 */
export function useComposedInputReference(external?: Ref<HTMLInputElement>): RefObject<HTMLInputElement | null> {
  const internal = useRef<HTMLInputElement>(null);

  useLayoutEffect(() => {
    if (!external) {
      return;
    }
    const node = internal.current;
    if (typeof external === "function") {
      // React 19 callback refs may return their own cleanup; we drive the
      // lifecycle ourselves, so discard it rather than returning it.
      external(node);
      return () => {
        // eslint-disable-next-line unicorn/no-null -- React's ref contract detaches with null, not undefined.
        external(null);
      };
    }
    external.current = node;
    return () => {
      // eslint-disable-next-line unicorn/no-null -- as above.
      external.current = null;
    };
  });

  return internal;
}
